import { createContext, useContext, useEffect, useMemo, useState, useRef, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { performSync, initialSync } from '../lib/sync/sync-engine'
import { getSyncQueueStats, clearSyncedItems } from '../lib/sync/sync-queue'
import { subscribeToTasks, subscribeToProjects, subscribeToSections } from '../lib/supabase/queries'
import { isSupabaseAvailable } from '../lib/supabase/client'
import { useAuth } from './AuthProvider'
import type { SyncState, SyncResult, SyncOptions } from '../lib/sync/types'

/**
 * Sync Context Provider
 * Manages sync state, auto-sync polling, and Supabase Realtime subscriptions
 */

interface SyncContextValue {
  syncState: SyncState
  isOnline: boolean
  isRealtimeConnected: boolean
  isSupabaseReachable: boolean
  nextRetryIn: number // seconds until next reconnect attempt

  // Sync actions
  sync: (options?: SyncOptions) => Promise<SyncResult>
  enableAutoSync: () => void
  disableAutoSync: () => void
  isAutoSyncEnabled: boolean
}

/** Check if a sync error string indicates a network/connectivity problem */
function isNetworkError(error: string): boolean {
  const patterns = ['fetch', 'network', 'timeout', 'econnrefused', 'err_name', 'failed to fetch', 'load failed']
  const lower = error.toLowerCase()
  return patterns.some(p => lower.includes(p))
}

/**
 * Returns true when the sync result is "another sync is already running" —
 * not a real failure, just a deferral. The other trigger (focus-gained,
 * online, auto-sync interval) will complete the work, so wake-sync should
 * NOT retry and must not raise a user-visible toast.
 */
function isSyncDeferred(errors: readonly string[]): boolean {
  return errors.length > 0 && errors.every(e => e === 'Sync already in progress')
}

// Exported for components that render outside the provider tree (e.g. Titlebar)
// and need safe access via useContext() which returns undefined instead of throwing.
export const SyncContext = createContext<SyncContextValue | undefined>(undefined)

interface SyncProviderProps {
  children: ReactNode
  autoSyncInterval?: number // in milliseconds (default: 5 minutes)
}

export function SyncProvider({ children, autoSyncInterval = 5 * 60 * 1000 }: SyncProviderProps) {
  const { t } = useTranslation()
  const { user, isAuthenticated } = useAuth()
  const lastSyncAtRef = useRef<Date | null>(null)
  const [syncState, setSyncState] = useState<SyncState>({
    status: 'idle',
    pendingChanges: 0,
  })
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const isOnlineRef = useRef(isOnline)
  isOnlineRef.current = isOnline
  const [isAutoSyncEnabled, setIsAutoSyncEnabled] = useState(false)
  const autoSyncTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false)
  const [isSupabaseReachable, setIsSupabaseReachable] = useState(true)
  const [nextRetryIn, setNextRetryIn] = useState(0)

  // Keep latest auth/auto-sync flags in a ref so the online/offline listener
  // can read them without re-subscribing on every state tick.
  const listenerStateRef = useRef({ isAuthenticated, isAutoSyncEnabled })
  listenerStateRef.current = { isAuthenticated, isAutoSyncEnabled }

  // Monitor online/offline status — attach once for the provider's lifetime.
  useEffect(() => {
    const handleOnline = () => {
      console.log('[SyncProvider] 🟢 Browser online event — navigator.onLine:', navigator.onLine)
      setIsOnline(true)

      // Trigger sync when coming back online
      const { isAuthenticated: authed, isAutoSyncEnabled: autoOn } = listenerStateRef.current
      if (authed && autoOn) {
        syncOpRef.current()
      }
    }

    const handleOffline = () => {
      console.log('[SyncProvider] 🔴 Browser offline event — navigator.onLine:', navigator.onLine)
      console.trace('[SyncProvider] offline event stack trace')
      setIsOnline(false)
      setIsSupabaseReachable(false)
      setSyncState((prev) => ({ ...prev, status: 'offline' }))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Sync on tab refocus after inactivity (e.g. returning to a tab left open overnight).
  // Only triggers if >5s have passed since the last successful sync, to avoid
  // redundant syncs when quickly switching between tabs.
  const STALE_SYNC_THRESHOLD_MS = 5_000
  // Wake-up resilience: with OS background throttling active, the first sync
  // attempt right after visibilitychange can hit a half-thawed network stack.
  // A small grace period lets sockets/DNS warm up; a silent retry absorbs any
  // transient failure so the user only sees a toast on a real outage.
  const WAKE_SYNC_GRACE_MS = 800
  const WAKE_SYNC_RETRY_DELAY_MS = 1500
  // Keep latest `t` in a ref so the listener reads the current translator
  // without re-subscribing when the language changes.
  const tRef = useRef(t)
  tRef.current = t
  useEffect(() => {
    let graceTimer: ReturnType<typeof setTimeout> | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    const attemptWakeSync = async (isRetry: boolean): Promise<void> => {
      // Skip entirely if we know we're offline. The online event handler will
      // trigger a sync as soon as connectivity returns, so no user-visible toast.
      if (!navigator.onLine) return

      try {
        const result = await syncOpRef.current()
        if (result.success) return
        // Another trigger (focus-gained, online, interval) is already running
        // a sync. Our goal is met — skip retry and toast entirely.
        if (isSyncDeferred(result.errors)) return
        if (!isRetry) {
          retryTimer = setTimeout(() => {
            retryTimer = null
            attemptWakeSync(true)
          }, WAKE_SYNC_RETRY_DELAY_MS)
          return
        }
        // Retry also failed. Suppress the toast if the failure clearly stems
        // from the network (user already knows — e.g. disabled Wi-Fi). The
        // online event will re-sync once connectivity returns.
        if (!navigator.onLine || result.errors.some(isNetworkError)) return
        toast.error(tRef.current('sync.resumeFailed'))
      } catch (error) {
        if (!isRetry) {
          retryTimer = setTimeout(() => {
            retryTimer = null
            attemptWakeSync(true)
          }, WAKE_SYNC_RETRY_DELAY_MS)
          return
        }
        if (!navigator.onLine || isNetworkError(String(error))) return
        toast.error(tRef.current('sync.resumeFailed'))
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        // Leaving the tab — cancel any pending wake-sync so it doesn't fire
        // in the background and defeat the point of OS throttling.
        if (graceTimer) { clearTimeout(graceTimer); graceTimer = null }
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
        return
      }
      const { isAuthenticated: authed, isAutoSyncEnabled: autoOn } = listenerStateRef.current
      if (!authed || !autoOn) return

      const elapsed = lastSyncAtRef.current
        ? Date.now() - lastSyncAtRef.current.getTime()
        : Infinity
      if (elapsed < STALE_SYNC_THRESHOLD_MS) return

      // Grace period: give the OS a moment to thaw the network stack before
      // hitting Supabase.
      if (graceTimer) clearTimeout(graceTimer)
      graceTimer = setTimeout(() => {
        graceTimer = null
        attemptWakeSync(false)
      }, WAKE_SYNC_GRACE_MS)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (graceTimer) clearTimeout(graceTimer)
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [])

  // Update pending changes count
  useEffect(() => {
    const updatePendingCount = async () => {
      const stats = await getSyncQueueStats()
      setSyncState((prev) => ({ ...prev, pendingChanges: stats.pending }))
    }

    updatePendingCount()

    // Update every 30 seconds
    const interval = setInterval(updatePendingCount, 30000)
    return () => clearInterval(interval)
  }, [])

  // Perform sync operation
  const performSyncOperation = useCallback(
    async (options?: SyncOptions): Promise<SyncResult> => {
      // Can't sync if not authenticated
      if (!isAuthenticated || !user) {
        return {
          success: false,
          synced: { created: 0, updated: 0, deleted: 0 },
          conflicts: [],
          errors: ['Not authenticated'],
        }
      }

      // Can't sync if offline
      if (!isOnline) {
        setSyncState((prev) => ({ ...prev, status: 'offline' }))
        return {
          success: false,
          synced: { created: 0, updated: 0, deleted: 0 },
          conflicts: [],
          errors: ['Offline'],
        }
      }

      try {
        setSyncState((prev) => ({ ...prev, status: 'syncing', error: undefined }))

        const result = await performSync(options)

        if (result.success) {
          lastSyncAtRef.current = new Date()
          setIsSupabaseReachable(true)
          setSyncState({
            status: 'synced',
            lastSyncAt: lastSyncAtRef.current,
            pendingChanges: 0,
          })

          // Cleanup old synced items
          await clearSyncedItems(7)
        } else {
          // Check if failure is due to network/connectivity
          if (result.errors.some(e => isNetworkError(e))) {
            console.log('[SyncProvider] 🔴 Supabase unreachable — sync errors:', result.errors)
            setIsSupabaseReachable(false)
          }
          setSyncState((prev) => ({
            ...prev,
            status: 'error',
            error: result.errors.join(', '),
          }))
        }

        return result
      } catch (error) {
        const errorStr = String(error)
        if (isNetworkError(errorStr)) {
          console.log('[SyncProvider] 🔴 Supabase unreachable — caught error:', errorStr)
          setIsSupabaseReachable(false)
        }
        setSyncState((prev) => ({
          ...prev,
          status: 'error',
          error: errorStr,
        }))

        return {
          success: false,
          synced: { created: 0, updated: 0, deleted: 0 },
          conflicts: [],
          errors: [errorStr],
        }
      }
    },
    [isAuthenticated, user, isOnline]
  )

  // Enable auto-sync
  const enableAutoSync = useCallback(() => {
    if (!isAuthenticated) {
      console.warn('[Sync] Cannot enable auto-sync: not authenticated')
      return
    }

    setIsAutoSyncEnabled(true)

    // Clear existing timer
    if (autoSyncTimerRef.current) {
      clearInterval(autoSyncTimerRef.current)
    }

    // Upload any local-only tasks (no cloudId), then run regular sync
    const startSync = async () => {
      if (user?.id) {
        try {
          await initialSync(user.id)
        } catch (err) {
          console.error('[Sync] initialSync failed:', err)
        }
      }
      syncOpRef.current()
    }
    startSync()

    // Set up periodic sync (use refs to avoid stale closures in long-lived interval)
    autoSyncTimerRef.current = setInterval(() => {
      if (isOnlineRef.current) {
        syncOpRef.current()
      }
    }, autoSyncInterval)
  }, [isAuthenticated, user, autoSyncInterval])

  // Disable auto-sync
  const disableAutoSync = useCallback(() => {
    setIsAutoSyncEnabled(false)

    if (autoSyncTimerRef.current) {
      clearInterval(autoSyncTimerRef.current)
      autoSyncTimerRef.current = null
    }

  }, [])

  // Auto-enable sync when user logs in
  useEffect(() => {
    if (isAuthenticated && !isAutoSyncEnabled) {
      enableAutoSync()
    } else if (!isAuthenticated && isAutoSyncEnabled) {
      // Disable sync when user logs out
      disableAutoSync()
    }
  }, [isAuthenticated])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSyncTimerRef.current) {
        clearInterval(autoSyncTimerRef.current)
      }
    }
  }, [])

  // ==================== REALTIME SUBSCRIPTIONS ====================

  // Ref to hold the latest performSyncOperation (avoids stale closure in debounce)
  const syncOpRef = useRef(performSyncOperation)
  syncOpRef.current = performSyncOperation

  // Debounced sync triggered by Realtime events
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const triggerRealtimeSync = useCallback(() => {
    // Debounce: batch rapid Realtime events into a single sync
    if (realtimeDebounceRef.current) {
      clearTimeout(realtimeDebounceRef.current)
    }
    realtimeDebounceRef.current = setTimeout(() => {
      realtimeDebounceRef.current = null
      syncOpRef.current().catch((err) =>
        console.error('[Realtime] Sync triggered by Realtime failed:', err)
      )
    }, 300)
  }, [])

  // Track previous Realtime connection state for reconnect catch-up
  const wasRealtimeConnectedRef = useRef(false)

  useEffect(() => {
    if (isRealtimeConnected && !wasRealtimeConnectedRef.current) {
      // Just reconnected after a disconnect — catch up on missed events
      syncOpRef.current().catch(() => {})
    }
    wasRealtimeConnectedRef.current = isRealtimeConnected
  }, [isRealtimeConnected])

  // Subscribe to Supabase Realtime channels when authenticated
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !isSupabaseAvailable()) {
      setIsRealtimeConnected(false)
      return
    }

    const unsubTasks = subscribeToTasks(
      user.id,
      triggerRealtimeSync,
      (status) => {
        const connected = status === 'SUBSCRIBED'
        setIsRealtimeConnected(connected)
        if (connected) setIsSupabaseReachable(true)
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setIsSupabaseReachable(false)
      }
    )

    const unsubProjects = subscribeToProjects(
      user.id,
      triggerRealtimeSync
    )

    const unsubSections = subscribeToSections(
      user.id,
      triggerRealtimeSync
    )

    return () => {
      unsubTasks()
      unsubProjects()
      unsubSections()
      setIsRealtimeConnected(false)
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current)
      }
    }
  }, [isAuthenticated, user?.id, triggerRealtimeSync])

  // ==================== FOCUS SYNC (Electron) ====================

  // Sync when the Electron window regains focus after inactivity
  const focusSyncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isAuthenticated || !isAutoSyncEnabled) return

    const handleFocusGained = () => {
      // Debounce: max one focus-sync per 2 seconds
      if (focusSyncDebounceRef.current) return

      // Skip if synced recently (consistent with visibilitychange handler)
      const elapsed = lastSyncAtRef.current
        ? Date.now() - lastSyncAtRef.current.getTime()
        : Infinity
      if (elapsed < STALE_SYNC_THRESHOLD_MS) return

      focusSyncDebounceRef.current = setTimeout(() => {
        focusSyncDebounceRef.current = null
      }, 2000)

      syncOpRef.current().catch(() => {})
    }

    // Electron: listen for IPC from main process
    let cleanup: (() => void) | undefined
    if (window.api?.receive) {
      cleanup = window.api.receive('window:focus-gained', handleFocusGained)
    }

    return () => {
      cleanup?.()
      if (focusSyncDebounceRef.current) {
        clearTimeout(focusSyncDebounceRef.current)
        focusSyncDebounceRef.current = null
      }
    }
  }, [isAuthenticated, isAutoSyncEnabled])

  // ==================== RETRY COUNTDOWN ====================

  // When offline/unreachable, count down to next reconnect attempt
  const RETRY_INTERVAL = 30 // seconds
  useEffect(() => {
    const isDisconnected = !isOnline || !isSupabaseReachable
    if (!isDisconnected || !isAuthenticated) {
      setNextRetryIn(0)
      return
    }

    setNextRetryIn(RETRY_INTERVAL)
    const interval = setInterval(() => {
      setNextRetryIn(prev => {
        if (prev <= 1) {
          // Trigger a sync attempt
          syncOpRef.current().catch(() => {})
          return RETRY_INTERVAL
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isOnline, isSupabaseReachable, isAuthenticated])

  const value = useMemo<SyncContextValue>(() => ({
    syncState,
    isOnline,
    isRealtimeConnected,
    isSupabaseReachable,
    nextRetryIn,
    sync: performSyncOperation,
    enableAutoSync,
    disableAutoSync,
    isAutoSyncEnabled,
  }), [
    syncState,
    isOnline,
    isRealtimeConnected,
    isSupabaseReachable,
    nextRetryIn,
    performSyncOperation,
    enableAutoSync,
    disableAutoSync,
    isAutoSyncEnabled,
  ])

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}

// Custom hook to use sync context
export function useSync(): SyncContextValue {
  const context = useContext(SyncContext)

  if (context === undefined) {
    throw new Error('useSync must be used within a SyncProvider')
  }

  return context
}
